import { Component, computed, effect, inject, signal, PLATFORM_ID, afterNextRender, Injector, viewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { ResponsiveImageComponent } from '@components/responsive-image/responsive-image.component';
import { AccentService } from '@services/accent-service.service';

@Component({
  selector: 'accent-switcher',
  templateUrl: './accent-switcher.component.html',
  styleUrls: ['./accent-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ResponsiveImageComponent],
})
export class AccentSwitcherComponent {
  private readonly accent = inject(AccentService);
  private readonly injector = inject(Injector);

  private platformId = inject<Object>(PLATFORM_ID);
  isBrowser = isPlatformBrowser(this.platformId);

  fileUpload = viewChild<ElementRef<HTMLInputElement>>('fileUpload');

  // keep names used by template
  readonly images = computed(() => this.accent.images); // string[]
  readonly activeIndex = computed(() => this.accent.activeKey());
  readonly customImage = computed(() => this.accent.customImage());

  private readonly hasAppliedTheme = signal(false);

  titleMappings: { [key: string]: string } = {
    primary: 'Dartegnian Blue',
    secondary: 'Vibrant Green',
    tertiary: 'Filling Station Purple',
  };

  constructor() {
    effect(() => {
      if (!this.isBrowser) return;

      const isCustom = this.activeIndex() === 'custom';
      const okToApply = isCustom && !this.accent.isPlaying;

      if (!okToApply) {
        this.hasAppliedTheme.set(false);
        return;
      }

      if (!this.hasAppliedTheme()) {
        this.hasAppliedTheme.set(true);
        afterNextRender(() => this.accent.setThemeFromM3(), {injector: this.injector});
      }
    });
  }

  changeAccent(imageIndex: number, imageName: string): void {
    if (imageName !== this.activeIndex()) {
      this.accent.setAccent(imageIndex);
    }
  }

  triggerFileUpload(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.fileUpload()?.nativeElement) {
      this.fileUpload()!.nativeElement.click();
    }
  }

  // your template calls this, so keep it
  setCustomImage(): void {
    if (this.accent.customImage()) {
      this.accent.setAccent(0);
    }
  }

  async onFileSelected(event: any) {
    const fileInput = event?.target;
    const file: File | undefined = fileInput?.files?.[0];
    if (!file) return;

    const dataUrl = await this.getFileDataUrl(file);
    this.accent.setCustomImage(dataUrl);

    // Reset the input value so the exact same file can be selected again
    if (fileInput) {
      fileInput.value = '';
    }
  }

  removeCustomImage() {
    this.accent.setCustomImage(null, true);

    if (this.accent.activeIndex() === 0) {
      this.changeAccent(1, 'primary');
    }

    if (this.fileUpload()?.nativeElement) {
      this.fileUpload()!.nativeElement.value = '';
    }
  }

  private getFileDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = (e: any) => reject(e.target.error);
      reader.readAsDataURL(file);
    });
  }
}
